/**
 * Fadenkreuz und Ablesefenster (HM-054).
 *
 * **Ein Diagramm im Browser ist bedienbar, oder es ist eine Abbildung.** Der
 * Unterschied entscheidet, ob jemand die Spitze um 7:14 Uhr benennen kann oder
 * nur sieht, dass es eine gab.
 *
 * Vier Regeln stecken in dieser Datei:
 *
 * **Das Fadenkreuz findet die Zeit, nicht die Kurve.** Gezeigt wird der
 * Zeitpunkt, der dem Zeiger am nächsten liegt — niemand trifft eine zwei
 * Bildpunkte breite Linie, und bei mehreren Reihen müsste er es mehrfach.
 *
 * **Ein Fenster, alle Reihen.** Wer die Werte zweier Reihen zum selben
 * Zeitpunkt vergleichen will, bekommt sie zusammen und nicht nacheinander.
 *
 * **Name links, Wert rechtsbündig** (HM-142, wie im Entwurf): Farbquadrat, Name,
 * dann die Zahl in einer Flucht am rechten Rand — so stehen die Werte mehrerer
 * Reihen untereinander und lassen sich vergleichen.
 *
 * **Bei einem Stapel steht die eigene Zahl da, nicht die Summe darunter.** Die
 * Matrix, aus der gelesen wird, trägt aufsummierte Spalten — sie ist das, was
 * gezeichnet wird. Ohne die Rückrechnung (`unstackValue`) stünde unter
 * „Netzanteil" der ganze Verbrauch. Die Summe selbst gibt es weiter, aber als
 * eigene Zeile mit eigenem Namen; die Höhe des Stapels ist eine Aussage, sie
 * gehört nur nicht der obersten Reihe.
 *
 * **Eine Lücke sagt „keine Messung"** und nicht „0" und auch nicht „—". Der
 * Gedankenstrich ist die bequeme Schreibweise für beides, und genau deshalb
 * taugt er hier nicht (Regel 8).
 *
 * **Und es bleibt im Bild** (HM-096). Auf einem Telefon ist die Zeichenfläche
 * schmaler als das Fenster; die Seite zu wechseln reicht dort nicht.
 */
import type uPlot from "uplot";
import { unstackValue } from "./stack.js";

export interface TooltipRow {
  /** Der Index in `u.data` — Bandkanten und Reihen liegen dort gemischt. */
  readonly slot: number;
  readonly label: string;
  readonly color: string;
  /**
   * Bei einem Stapel der Slot der Reihe darunter: Sein Wert wird abgezogen.
   * `null` heißt „in der Spalte steht bereits der eigene Wert".
   */
  readonly beneath?: number | null;
  /** Die Abdeckung je Zeitpunkt; `null` heißt vollständig (HM-105). */
  readonly coverage?: readonly (number | null)[];
  /** Der Status je Zeitpunkt; `null` heißt gemessen (HM-115). */
  readonly quality?: readonly (string | null)[];
  /** Rechts steht eine andere Einheit — sie bekommt `rightValue` (HM-137). */
  readonly axis?: "left" | "right";
}

/**
 * Die Höhe des Stapels — als eigene Zeile unter dem Strich.
 *
 * Sie steht nur, wo die Summe eine Bedeutung hat: PV-Anteil und Netzanteil
 * übereinander **sind** der Verbrauch. Ohne Stapelung gibt es sie nicht — eine
 * Summe über Reihen, die nichts miteinander zu tun haben, wäre eine Rechnung,
 * die niemand bestellt hat (Regel 9).
 */
export interface TooltipTotal {
  /** Der Slot mit der obersten, also vollen Summe. */
  readonly slot: number;
  readonly label: string;
}

export interface TooltipState {
  readonly rows: readonly TooltipRow[];
  /** Gesetzt bei einem Stapel: die Summenzeile unter dem Strich. */
  readonly total?: TooltipTotal | null;
  /** Wie der Zeitpunkt geschrieben wird — in der Zeitzone der Anlage. */
  readonly time: (at: number) => string;
  readonly value: (value: number) => string;
  /** Das Format der rechten Achse; ohne Angabe gilt `value`. */
  readonly rightValue?: (value: number) => string;
  /**
   * Welcher Index zu einer Zeigerposition gehört (HM-099). Ohne Angabe der
   * nächstgelegene Zeitpunkt — richtig für eine Linie. Über Balken ist es der
   * **Abschnitt unter dem Zeiger**, und rechts vom letzten gibt es keinen:
   * `null` schließt das Fenster.
   */
  readonly locate?: ((at: number, times: readonly number[]) => number | null) | undefined;
}

/** Abstand zwischen Zeiger und Fenster — in beide Richtungen derselbe (Entwurf: 10). */
const GAP = 10;

/**
 * Wohin das Ablesefenster gehört (HM-096).
 *
 * Rechts vom Zeiger, solange es dort **ganz** hineinpasst; sonst links davon.
 * Und in keinem Fall über den Rand der Zeichenfläche hinaus: Auf einem Telefon
 * ist sie schmaler als das Fenster, und ein Fenster, das man an den
 * interessanten Rand schiebt, ist an genau der Stelle unlesbar, an der man es
 * aufmacht. Die Seite zu wechseln allein reicht dafür nicht — ist das Fenster
 * breiter als der Platz links des Zeigers, landet es hinter der Kante.
 *
 * **Der Rand gewinnt gegen den Abstand zum Zeiger.** Passt es in keine der
 * beiden Richtungen, steht es über der Kurve statt halb außerhalb; das
 * Fadenkreuz sagt weiter, welcher Zeitpunkt gemeint ist.
 */
export function tooltipLeft(cursor: number, width: number, room: number, gap = GAP): number {
  const rechts = cursor + gap;
  const gewuenscht = rechts + width <= room ? rechts : cursor - width - gap;
  return Math.min(Math.max(gewuenscht, 0), Math.max(room - width, 0));
}

export function tooltipPlugin(read: () => TooltipState): uPlot.Plugin {
  let box: HTMLDivElement | null = null;

  return {
    hooks: {
      init: (u) => {
        box = document.createElement("div");
        box.className = "hm-chart-tip";
        box.style.display = "none";
        // Das Fenster gehört nicht in den Lesefluss: Es wiederholt, was die
        // Tabelle unter dem Diagramm ohnehin führt.
        box.setAttribute("aria-hidden", "true");
        u.over.append(box);
      },

      destroy: () => {
        box?.remove();
        box = null;
      },

      setCursor: (u) => {
        if (box === null) return;
        const index = u.cursor.idx;
        const left = u.cursor.left;

        if (index === null || index === undefined || left === undefined || left < 0) {
          box.style.display = "none";
          return;
        }

        const state = read();
        const times = u.data[0] as readonly number[];
        const located = state.locate === undefined ? index : state.locate(u.posToVal(left, "x"), times);
        const at = located === null ? undefined : times[located];
        if (located === null || at === undefined) {
          box.style.display = "none";
          return;
        }
        const slot = located;

        const total = state.total ?? null;
        box.replaceChildren(
          heading(state.time(at)),
          ...state.rows.map((row) =>
            line(
              row,
              unstackValue(u.data[row.slot]?.[slot], readBeneath(u, row, slot)),
              row.axis === "right" ? (state.rightValue ?? state.value) : state.value,
              row.quality?.[slot] ?? null,
            ),
          ),
          ...(total === null
            ? []
            : [sum(total.label, u.data[total.slot]?.[slot] ?? null, state.value)]),
        );
        box.style.display = "block";

        box.style.left = `${tooltipLeft(left, box.offsetWidth, u.over.clientWidth)}px`;
        box.style.top = "0px";
      },
    },
  };
}

/**
 * Der Wert der Reihe darunter — `undefined`, wo es keine gibt: Dann steht in
 * der Spalte schon der eigene Wert, und `unstackValue` zieht nichts ab. Das ist
 * etwas anderes als `null`, was „darunter fehlt eine Messung" hieße und den
 * eigenen Wert damit unbekannt macht.
 */
function readBeneath(u: uPlot, row: TooltipRow, index: number): number | null | undefined {
  const beneath = row.beneath;
  if (beneath === null || beneath === undefined) return undefined;
  return u.data[beneath]?.[index] ?? null;
}

function heading(text: string): HTMLElement {
  const element = document.createElement("div");
  element.className = "hm-chart-tip-time";
  // `textContent` und nie zusammengesetztes HTML: Ein Reihenname kommt am Ende
  // aus dem Katalog eines Geräts, und der schreibt ihn selbst.
  element.textContent = text;
  return element;
}

/**
 * „bei 79 % Abdeckung" — der Zusatz zu einer Teilsumme (HM-105). Im
 * Ablesefenster steht er seit HM-143 nicht mehr; die Worte bleiben für jede
 * Stelle, die eine Teilsumme benennen muss.
 */
export function coverageNote(coverage: number): string {
  return `bei ${Math.round(coverage * 100)} % Abdeckung`;
}

/**
 * „Ersatzwert, interpoliert" — der Zusatz zu einem Wert, der nicht gemessen
 * ist (HM-115). `null` für einen gemessenen. Das Verfahren steht dabei, weil
 * es verschieden viel verspricht: Zwischen zwei Nachbarn gerade gezogen ist
 * etwas anderes als nach einem Vergleichstag geformt.
 */
export function qualityNote(quality: string | null): string | null {
  switch (quality) {
    case "interpolated":
      return "Ersatzwert, interpoliert";
    case "estimated":
      return "Ersatzwert, geschätzt";
    case "suspect":
      return "unplausibel";
    default:
      return null;
  }
}

function line(
  row: TooltipRow,
  value: number | null | undefined,
  format: (value: number) => string,
  quality: string | null = null,
): HTMLElement {
  const element = document.createElement("div");
  element.className = "hm-chart-tip-row";

  const key = document.createElement("span");
  key.className = "hm-chart-tip-key";
  key.style.background = row.color;

  const amount = document.createElement("span");
  amount.className = "hm-chart-tip-value";
  amount.textContent =
    value === null || value === undefined || !Number.isFinite(value)
      ? "keine Messung"
      : format(value);

  const label = document.createElement("span");
  label.className = "hm-chart-tip-label";
  label.textContent = row.label;

  element.append(key, label, amount);

  /* Eine Abdeckung steht hier nicht mehr (HM-143, Auftraggeber): Eine
     Teilsumme zeigt das Diagramm selbst — blasser gezeichnet. Ein Ersatzwert
     bleibt genannt; ihn sieht man dem Wert sonst nirgends an (ADR 0034). */
  const substitute = value === null || value === undefined ? null : qualityNote(quality);
  if (substitute !== null) {
    const note = document.createElement("span");
    note.className = "hm-chart-tip-note";
    note.textContent = substitute;
    element.append(note);
  }
  return element;
}

/**
 * Die Summenzeile. Sie trägt keinen Farbklecks: Zu ihr gehört keine Fläche im
 * Bild, und ein Punkt in irgendeiner Farbe sähe aus wie eine weitere Reihe.
 */
function sum(
  label: string,
  value: number | null,
  format: (value: number) => string,
): HTMLElement {
  const element = document.createElement("div");
  element.className = "hm-chart-tip-row hm-chart-tip-total";

  const amount = document.createElement("span");
  amount.className = "hm-chart-tip-value";
  amount.textContent =
    value === null || !Number.isFinite(value) ? "keine Messung" : format(value);

  const name = document.createElement("span");
  name.className = "hm-chart-tip-label";
  name.textContent = label;

  element.append(name, amount);
  return element;
}
